// Using any types since pdfMake is loaded dynamically from CDN
type Content = any
type ContentTable = any
type TDocumentDefinitions = any
type TableCell = any

declare global {
  interface Window {
    pdfMake: any
  }
}

// Escapes a string for safe use as a CSS attribute selector value (inside double quotes)
function escapeAttributeSelectorValue(value: string): string {
  // Escape backslash first, then double quote (order matters)
  return value.replace(/\\/g, '\\\\').replace(/"/g, '\\"')
}

// Collects pdfMake text runs from a node, preserving the pink (#F7006B) accents
// on nested spans (e.g. inside a flex <div> wrapper) that a shallow childNodes
// pass would otherwise flatten to plain black text.
const collectColoredRuns = (
  node: Node,
  fontSize: number | undefined,
  bold: boolean,
): any[] => {
  const runs: any[] = []
  node.childNodes.forEach(child => {
    if (child.nodeType === Node.TEXT_NODE) {
      const text = child.textContent || ''
      // Keep meaningful text (with its spaces); skip pure-whitespace nodes.
      if (text.replace(/\s+/g, '').length > 0) {
        runs.push({ text, fontSize, bold })
      }
    } else if (child.nodeType === Node.ELEMENT_NODE) {
      const el = child as HTMLElement
      const style = el.getAttribute('style') || ''
      // Match a `color:` declaration without tripping on `background-color:`
      // (the FM PIK sub-row carries both on the same row). Any explicitly
      // colored inline element keeps its color in the pdfmake output — e.g.
      // pink #F7006B accents and the blue "PIK detail" label (LOS-4895).
      const colorMatch = style.match(/(?:^|;)\s*color:\s*(#[0-9a-fA-F]{6})/)
      if (colorMatch) {
        const text = el.textContent?.trim()
        if (text)
          runs.push({
            text,
            fontSize,
            bold: bold || /font-weight:\s*bold/.test(style),
            color: colorMatch[1].toUpperCase(),
          })
      } else {
        // Propagate inline bolding into the recursion so uncolored-but-bold
        // spans (e.g. the FM PIK sub-row's Cash/PIK split) keep their weight.
        runs.push(
          ...collectColoredRuns(
            el,
            fontSize,
            bold || /font-weight:\s*bold/.test(style),
          ),
        )
      }
    }
  })
  return runs
}

const loadPdfMake = async (): Promise<any> => {
  if (window.pdfMake) {
    return window.pdfMake
  }

  return new Promise((resolve, reject) => {
    const pdfMakeScript = document.createElement('script')
    pdfMakeScript.src = '/vendor/pdfmake/pdfmake.min.js'
    pdfMakeScript.onload = () => {
      const vfsFontsScript = document.createElement('script')
      vfsFontsScript.src = '/vendor/pdfmake/vfs_fonts.js'
      vfsFontsScript.onload = () => {
        if (window.pdfMake) {
          resolve(window.pdfMake)
        } else {
          reject(new Error('pdfMake failed to load'))
        }
      }
      vfsFontsScript.onerror = () =>
        reject(new Error('Failed to load vfs_fonts.js'))
      document.head.appendChild(vfsFontsScript)
    }
    pdfMakeScript.onerror = () => reject(new Error('Failed to load pdfmake.js'))
    document.head.appendChild(pdfMakeScript)
  })
}

const imageToDataURL = async (
  url: string,
  sourceDocument?: Document,
): Promise<string> => {
  try {
    if (url.startsWith('data:')) {
      return url
    }

    // If we have access to the source document, try to get the image from the DOM
    // Images should be loaded with crossorigin="anonymous" to allow canvas access
    if (sourceDocument) {
      // Try to find the image element - escape special characters in selector
      let imgElement = sourceDocument.querySelector(
        `img[src="${escapeAttributeSelectorValue(url)}"]`,
      ) as HTMLImageElement

      // If not found by exact match, try finding by direct src comparison
      if (!imgElement) {
        const allImages = Array.from(
          sourceDocument.querySelectorAll('img'),
        ) as HTMLImageElement[]
        imgElement = allImages.find(img => img.src === url) as HTMLImageElement
      }

      if (imgElement && imgElement.complete && imgElement.naturalWidth > 0) {
        try {
          const canvas = document.createElement('canvas')
          canvas.width = imgElement.naturalWidth
          canvas.height = imgElement.naturalHeight
          const ctx = canvas.getContext('2d')

          if (ctx && canvas.width > 0 && canvas.height > 0) {
            ctx.drawImage(imgElement, 0, 0)
            const dataUrl = canvas.toDataURL('image/png')
            if (dataUrl && dataUrl !== 'data:,' && dataUrl.length > 100) {
              return dataUrl
            }
          }
        } catch (canvasError) {
          console.warn('Failed to convert image via canvas:', canvasError)
        }
      }
    }

    // Fallback to fetch (will hit CORS for cross-origin images)
    const response = await fetch(url)
    const blob = await response.blob()

    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onloadend = () => resolve(reader.result as string)
      reader.onerror = reject
      reader.readAsDataURL(blob)
    })
  } catch {
    return 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII='
  }
}

export const parseHtmlToPdfContent = async (
  htmlContent: string,
  sourceDocument?: Document,
): Promise<Content[]> => {
  const parser = new DOMParser()
  const doc = parser.parseFromString(htmlContent, 'text/html')
  const body = doc.body

  const content = parseElement(body)
  // Use the sourceDocument (iframe's document) if provided, otherwise use parsed doc
  await convertImagesToDataURLs(content, sourceDocument || doc)

  return content
}

const convertImagesToDataURLs = async (
  content: any,
  sourceDocument?: Document,
): Promise<void> => {
  if (Array.isArray(content)) {
    await Promise.all(
      content.map(item => convertImagesToDataURLs(item, sourceDocument)),
    )
  } else if (content && typeof content === 'object') {
    if (
      content.image &&
      typeof content.image === 'string' &&
      !content.image.startsWith('data:')
    ) {
      content.image = await imageToDataURL(content.image, sourceDocument)
    }

    if (content.stack) {
      await convertImagesToDataURLs(content.stack, sourceDocument)
    }
    if (content.columns) {
      await convertImagesToDataURLs(content.columns, sourceDocument)
    }
    if (content.table && content.table.body) {
      await convertImagesToDataURLs(content.table.body, sourceDocument)
    }
    if (content.text && Array.isArray(content.text)) {
      await convertImagesToDataURLs(content.text, sourceDocument)
    }
  }
}

const parseElement = (
  element: Element,
  parentContext?: { sectionType?: 'boxed' | 'pink' },
): Content[] => {
  const content: Content[] = []
  const children = Array.from(element.children)
  const processedIndices = new Set<number>()

  const hasDirectText = Array.from(element.childNodes).some(
    node => node.nodeType === Node.TEXT_NODE && node.textContent?.trim(),
  )

  if (hasDirectText && children.length > 0) {
    const inlineContent: any[] = []
    element.childNodes.forEach(node => {
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent?.trim()
        if (text) {
          inlineContent.push(text)
        }
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const el = node as HTMLElement
        const tagName = el.tagName.toLowerCase()

        if (['text-field', 'date-field', 'signature-field'].includes(tagName)) {
          return
        }

        const nestedContent = parseElement(el)
        if (nestedContent.length > 0) {
          inlineContent.push(...nestedContent)
        } else {
          const text = el.textContent?.trim()
          if (text) {
            inlineContent.push(text)
          }
        }
      }
    })

    if (inlineContent.length > 0) {
      content.push({
        text: inlineContent,
        margin: [0, 0, 0, 1] as [number, number, number, number],
      })
    }
    return content
  }

  children.forEach((child, index) => {
    if (processedIndices.has(index)) return

    const tagName = child.tagName.toLowerCase()
    const classList = Array.from(child.classList)

    switch (tagName) {
      case 'h1': {
        const h1Text = child.textContent?.trim() || ''
        if (
          child.id === 'top-header' ||
          child.parentElement?.id === 'top-header'
        ) {
          content.push({
            text: h1Text,
            style: 'h1',
            margin: [0, 0, 0, 5] as [number, number, number, number],
          })
        } else {
          content.push({
            text: h1Text,
            style: 'h1',
            margin: [0, 0, 0, 8] as [number, number, number, number],
          })
        }
        break
      }
      case 'h2':
        content.push({
          text: child.textContent?.trim() || '',
          style: classList.includes('highlight-red') ? 'h2Red' : 'h2',
          margin: [0, 8, 0, 6] as [number, number, number, number],
        })
        break

      case 'h3':
        content.push({
          text: child.textContent?.trim() || '',
          style: 'h3',
          margin: [0, 6, 0, 3] as [number, number, number, number],
        })
        break

      case 'h4':
        content.push({
          text: child.textContent?.trim() || '',
          style: 'h4',
          margin: [0, 12, 0, 2] as [number, number, number, number],
        })
        break

      case 'p': {
        const pText = child.textContent?.trim()
        if (pText) {
          const spans = child.querySelectorAll('span')
          const isHighlightRed = classList.includes('highlight-red')
          const isDcFootnote = classList.includes('dc-footnote')

          if (spans.length > 0) {
            const textParts: any[] = []
            parseInlineElements(child, textParts)
            content.push({
              text: textParts,
              margin: [0, 1, 0, 1] as [number, number, number, number],
              color: isHighlightRed
                ? '#ee5959'
                : isDcFootnote
                  ? '#777'
                  : undefined,
              fontSize: isDcFootnote ? 6 : undefined,
            })
          } else {
            content.push({
              text: pText,
              margin: [0, 0, 0, 1] as [number, number, number, number],
              color: isHighlightRed
                ? '#ee5959'
                : isDcFootnote
                  ? '#777'
                  : undefined,
              fontSize: isDcFootnote ? 6 : undefined,
              alignment: classList.includes('fee-nil') ? ('center' as const) : undefined,
            })
          }
        }
        break
      }
      case 'table': {
        const table = parseTable(child as HTMLTableElement, parentContext)
        if (table) {
          content.push(table)
        }
        break
      }
      case 'img': {
        const img = child as HTMLImageElement
        if (img.src) {
          // Check if this image is in the top-header for special sizing
          const isInTopHeader = img.closest('#top-header') !== null
          content.push({
            image: img.src,
            width: isInTopHeader ? 60 : 120, // Smaller logo for header
            margin: [0, 0, 0, 0] as [number, number, number, number],
            alignment: isInTopHeader ? ('right' as const) : ('left' as const),
          })
        }
        break
      }
      case 'div': {
        if (child.id === 'top-header') {
          // LOS-6658: a draft FM stacks the NOT EFFECTIVE badge under the logo
          // inside .header-right. That group becomes the right column wholesale —
          // the image/non-image split below would drop the badge's text into the
          // title column on the left.
          const rightGroup = child.querySelector('.header-right')
          if (rightGroup) {
            const leftEl = child.cloneNode(true) as Element
            leftEl.querySelector('.header-right')?.remove()
            content.push({
              columns: [
                { stack: parseElement(leftEl), width: '*' },
                {
                  // pdfMake ignores CSS, so the badge's fill and border have to
                  // be built as content: a single-cell table is how you draw a
                  // box around text. Without this it prints as a bare coloured
                  // line, which reads as a caption rather than a stamp.
                  stack: parseElement(rightGroup).map((item: any) =>
                    item.text
                      ? {
                          table: {
                            body: [
                              [
                                {
                                  ...item,
                                  color: '#F7006B',
                                  bold: true,
                                  fontSize: 7,
                                  fillColor: '#FDEEF4',
                                  alignment: 'center',
                                  // Optical, not arithmetic, centring: the text
                                  // is all caps with no descenders, but pdfMake
                                  // still reserves descender space in the line
                                  // box, so equal padding leaves the glyphs
                                  // sitting high. More above than below evens it.
                                  margin: [6, 3.5, 6, 1] as [
                                    number,
                                    number,
                                    number,
                                    number,
                                  ],
                                },
                              ],
                            ],
                          },
                          layout: {
                            hLineWidth: () => 0.75,
                            vLineWidth: () => 0.75,
                            hLineColor: () => '#F7006B',
                            vLineColor: () => '#F7006B',
                            paddingLeft: () => 0,
                            paddingRight: () => 0,
                            paddingTop: () => 0,
                            paddingBottom: () => 0,
                          },
                          margin: [0, 3, 0, 0] as [
                            number,
                            number,
                            number,
                            number,
                          ],
                        }
                      : item,
                  ),
                  width: 'auto',
                },
              ],
              margin: [0, 0, 0, 5] as [number, number, number, number],
            })
            break
          }

          const headerContent = parseElement(child)
          const hasImage = headerContent.some((item: any) => item.image)

          if (hasImage) {
            content.push({
              columns: [
                {
                  stack: headerContent.filter((item: any) => !item.image),
                  width: '*',
                },
                {
                  stack: headerContent.filter((item: any) => item.image),
                  width: 'auto',
                },
              ],
              margin: [0, 0, 0, 5] as [number, number, number, number],
            })
          } else {
            content.push(...headerContent)
          }
        } else if (classList.includes('section')) {
          const sectionContent = parseElement(child, {
            sectionType: classList.includes('pink')
              ? 'pink'
              : classList.includes('boxed')
                ? 'boxed'
                : undefined,
          })
          if (sectionContent.length > 0) {
            // Check if section contains tables to adjust margins
            const containsTables = hasTableContent(sectionContent)
            const contentMargin = containsTables
              ? ([2, 2, 2, 2] as [number, number, number, number])
              : ([6, 6, 6, 6] as [number, number, number, number])

            if (classList.includes('boxed')) {
              // Remove bottom margin from the last item to avoid gap before the border
              const lastItem = sectionContent[sectionContent.length - 1]
              if (lastItem?.margin) {
                lastItem.margin = [
                  lastItem.margin[0],
                  lastItem.margin[1],
                  lastItem.margin[2],
                  0,
                ]
              }
              content.push({
                table: {
                  widths: ['*'],
                  body: [
                    [
                      {
                        stack: sectionContent,
                        fillColor: '#ffffff',
                        margin: contentMargin,
                      },
                    ],
                  ],
                },
                layout: {
                  fillColor: '#fafafa',
                  hLineWidth: () => 1,
                  vLineWidth: () => 1,
                  hLineColor: () => '#71c0ee',
                  vLineColor: () => '#71c0ee',
                  paddingLeft: () => 0,
                  paddingRight: () => 0,
                  paddingTop: () => 0,
                  paddingBottom: () => 0,
                },
                margin: [0, 0, 0, 12] as [number, number, number, number],
                unbreakable: true,
              } as any)
            } else if (classList.includes('pink')) {
              content.push({
                table: {
                  widths: ['*'],
                  body: [
                    [
                      {
                        stack: sectionContent,
                        fillColor: '#ffffff',
                        margin: contentMargin,
                      },
                    ],
                  ],
                },
                layout: {
                  fillColor: '#fafafa',
                  hLineWidth: () => 1,
                  vLineWidth: () => 1,
                  hLineColor: () => '#f7006b',
                  vLineColor: () => '#f7006b',
                  paddingLeft: () => 0,
                  paddingRight: () => 0,
                  paddingTop: () => 0,
                  paddingBottom: () => 0,
                },
                margin: [0, 0, 0, 12] as [number, number, number, number],
              } as any)
            } else if (classList.includes('unbreakable')) {
              content.push({
                stack: sectionContent,
                margin: [0, 0, 0, 12] as [number, number, number, number],
                unbreakable: true,
              } as any)
            } else {
              content.push({
                stack: sectionContent,
                margin: [0, 0, 0, 12] as [number, number, number, number],
              })
            }
          }
        } else if (classList.includes('info-boxes')) {
          const boxes = parseInfoBoxes(child)
          if (boxes.length > 0) {
            content.push(...boxes)
          }
        } else if (classList.includes('signature-box')) {
          // Look for adjacent signature-box elements to group together
          const signatureBoxes: Element[] = [child]

          // Check subsequent siblings for more signature boxes
          for (let i = index + 1; i < children.length; i++) {
            const nextChild = children[i]
            if (nextChild.classList.contains('signature-box')) {
              signatureBoxes.push(nextChild)
              processedIndices.add(i) // Mark as processed
            } else {
              break // Stop at first non-signature-box
            }
          }

          // If we have multiple signature boxes, display side by side
          if (signatureBoxes.length > 1) {
            const columns: any[] = []

            signatureBoxes.forEach(box => {
              const boxContent = parseElement(box)

              columns.push({
                table: {
                  widths: ['*'],
                  body: [
                    [
                      {
                        stack: boxContent,
                        margin: [6, 6, 6, 6] as [
                          number,
                          number,
                          number,
                          number,
                        ],
                      },
                    ],
                  ],
                },
                layout: {
                  hLineWidth: () => 1,
                  vLineWidth: () => 1,
                  hLineColor: () => '#cccccc',
                  vLineColor: () => '#cccccc',
                  paddingLeft: () => 0,
                  paddingRight: () => 0,
                  paddingTop: () => 0,
                  paddingBottom: () => 0,
                },
                width: '*',
              })
            })

            content.push({
              columns,
              columnGap: 8,
              margin: [0, 0, 0, 6] as [number, number, number, number],
              unbreakable: true,
            } as any)
          } else {
            // Single signature box
            const boxContent = parseElement(child)
            content.push({
              table: {
                widths: ['*'],
                body: [
                  [
                    {
                      stack: boxContent,
                      margin: [6, 6, 6, 6] as [number, number, number, number],
                    },
                  ],
                ],
              },
              layout: {
                hLineWidth: () => 1,
                vLineWidth: () => 1,
                hLineColor: () => '#cccccc',
                vLineColor: () => '#cccccc',
                paddingLeft: () => 0,
                paddingRight: () => 0,
                paddingTop: () => 0,
                paddingBottom: () => 0,
              },
              margin: [0, 0, 0, 6] as [number, number, number, number],
            })
          }
        } else if (classList.includes('highlight-red')) {
          content.push({
            text: child.textContent?.trim() || '',
            color: '#ee5959',
            bold: true,
            margin: [0, 3, 0, 3] as [number, number, number, number],
          })
        } else {
          content.push(...parseElement(child))
        }
        break
      }
      case 'br':
        content.push({
          text: '\n',
          margin: [0, 2, 0, 0] as [number, number, number, number],
        })
        break

      case 'style':
      case 'script':
      case 'link':
      case 'meta':
        break

      case 'svg':
        break

      case 'signature-field':
      case 'text-field':
      case 'date-field':
        break

      default:
        if (child.children.length > 0) {
          content.push(...parseElement(child))
        } else if (child.textContent?.trim()) {
          const parentTag = child.parentElement?.tagName.toLowerCase()
          if (parentTag !== 'style' && parentTag !== 'script') {
            content.push({ text: child.textContent.trim(), fontSize: 8 })
          }
        }
    }
  })

  return content
}

const parseInlineElements = (element: Element, textParts: any[]) => {
  element.childNodes.forEach(node => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent?.trim()
      if (text) {
        textParts.push(text)
      }
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as HTMLElement
      const tagName = el.tagName.toLowerCase()

      if (tagName === 'span') {
        const spanStyle = el.getAttribute('style') || ''
        const spanColor =
          spanStyle.includes('#F7006B') || spanStyle.includes('#f7006b')
            ? '#F7006B'
            : undefined
        textParts.push({
          text: el.textContent?.trim() || '',
          bold: true,
          color: spanColor,
        })
      } else {
        const text = el.textContent?.trim()
        if (text) {
          textParts.push(text)
        }
      }
    }
  })
}

/**
 * Check if content array contains table elements
 */
const hasTableContent = (content: Content[]): boolean => {
  return content.some(item => {
    if (item && typeof item === 'object') {
      if (item.table) return true
      if (item.stack && Array.isArray(item.stack))
        return hasTableContent(item.stack)
    }
    return false
  })
}

/**
 * Check if a DOM element contains a vertical table
 */
const containsVerticalTable = (element: Element): boolean => {
  return element.querySelector('table.vertical-table') !== null
}

/**
 * Parse info-boxes structure (commonly used in funding memos)
 */
// Funding Memo Fee Breakdown heading row: a fee heading on the left, its total or nil statement
// on the right. pdfMake has no vertical alignment inside table cells, so the two single-line
// cells are made the same height by margins instead: h3 renders at 10pt and body text at 8pt
// (lineHeight 1.3 → 13 vs 10.4), so the text row gets 1.3pt more above and below. A nil
// statement is also centred horizontally, mirroring the `.fee-nil` CSS the DocuSeal render uses.
const FEE_SECTION_HEADING_MARGIN: [number, number, number, number] = [0, 2, 0, 2]
const FEE_SECTION_TEXT_MARGIN: [number, number, number, number] = [0, 3.3, 0, 3.3]

const alignFeeSectionBox = (box: Element, boxContent: Content[]): Content[] =>
  boxContent.map(item => {
    if (!item || typeof item !== 'object') return item
    const aligned = {
      ...item,
      margin: item.style === 'h3' ? FEE_SECTION_HEADING_MARGIN : FEE_SECTION_TEXT_MARGIN,
    }
    if (box.classList.contains('fee-nil-box')) aligned.alignment = 'center'
    return aligned
  })

const parseInfoBoxes = (container: Element): Content[] => {
  const boxes: Content[] = []
  const infoBoxes = container.querySelectorAll('.info-box, .signature-box')
  const isFeeSectionRow = container.classList.contains('fee-section-boxes')

  if (infoBoxes.length > 1) {
    const columns: any[] = []

    infoBoxes.forEach(box => {
      const parsed = parseElement(box)
      const boxContent = isFeeSectionRow ? alignFeeSectionBox(box, parsed) : parsed
      const isSignatureBox = box.classList.contains('signature-box')
      const isHalfWidth = box.classList.contains('half-width')
      const hasVerticalTable = containsVerticalTable(box)

      if (isSignatureBox) {
        columns.push({
          stack: boxContent,
          width: isHalfWidth ? '49%' : '*',
        })
      } else {
        const boxMargin = hasVerticalTable
          ? ([4, 0, 0, 0] as [number, number, number, number])
          : ([4, 4, 4, 4] as [number, number, number, number])
        columns.push({
          table: {
            widths: ['*'],
            body: [
              [
                {
                  stack: boxContent,
                  margin: boxMargin,
                },
              ],
            ],
          },
          layout: {
            hLineWidth: () => 1,
            vLineWidth: () => 1,
            hLineColor: () => '#cccccc',
            vLineColor: () => '#cccccc',
            paddingLeft: () => 0,
            paddingRight: () => 0,
            paddingTop: () => 0,
            paddingBottom: () => 0,
          },
          width: '*',
          unbreakable: hasVerticalTable, // Keep info-boxes with vertical tables together
        })
      }
    })

    boxes.push({
      columns,
      columnGap: 8,
      margin: [0, 0, 0, 6] as [number, number, number, number],
    })
  } else if (infoBoxes.length === 1) {
    const boxContent = parseElement(infoBoxes[0])
    const isSignatureBox = infoBoxes[0].classList.contains('signature-box')
    const isHalfWidth = infoBoxes[0].classList.contains('half-width')
    const hasVerticalTable = containsVerticalTable(infoBoxes[0])

    if (isSignatureBox) {
      boxes.push({
        stack: boxContent,
        margin: [0, 0, 0, 6] as [number, number, number, number],
      })
    } else {
      const singleBoxMargin = hasVerticalTable
        ? ([4, 0, 0, 0] as [number, number, number, number])
        : ([4, 4, 4, 4] as [number, number, number, number])
      const boxTable = {
        table: {
          widths: ['*'],
          body: [
            [
              {
                stack: boxContent,
                margin: singleBoxMargin,
              },
            ],
          ],
        },
        layout: {
          hLineWidth: () => 1,
          vLineWidth: () => 1,
          hLineColor: () => '#cccccc',
          vLineColor: () => '#cccccc',
          paddingLeft: () => 0,
          paddingRight: () => 0,
          paddingTop: () => 0,
          paddingBottom: () => 0,
        },
        margin: [0, 0, 0, 6] as [number, number, number, number],
        unbreakable: hasVerticalTable, // Keep info-boxes with vertical tables together
      }

      // If half-width, wrap in columns layout to constrain to 50%
      if (isHalfWidth) {
        boxes.push({
          columns: [
            { ...boxTable, width: '49.1%' },
            { text: '', width: '49.1%' }, // Empty column to take remaining space
          ],
          columnGap: 0,
          margin: [0, 0, 0, 6] as [number, number, number, number],
        })
      } else {
        boxes.push(boxTable)
      }
    }
  }

  return boxes
}

// Map an FM table row's inline font-size to a pdfmake point size. Base data
// rows render at 7. `14px` marks emphasized rows (→ 11); `8px` marks the PIK
// detail sub-row, which must stay visibly SMALLER than the standard loan rows
// (AC-4, LOS-4895) — so it maps below the base size (→ 6).
export const rowFontSizeFromStyle = (rowStyle: string): number => {
  if (rowStyle.includes('font-size: 14px')) return 11
  if (rowStyle.includes('font-size: 8px')) return 6
  return 7
}

const parseTable = (
  table: HTMLTableElement,
  parentContext?: { sectionType?: 'boxed' | 'pink' },
): ContentTable | null => {
  const tbody = table.querySelector('tbody')
  const thead = table.querySelector('thead')
  const tableClasses = Array.from(table.classList)
  const isVerticalTable = tableClasses.includes('vertical-table')

  if (!tbody && !thead) return null

  const body: TableCell[][] = []

  if (thead) {
    const headerRows = thead.querySelectorAll('tr')
    headerRows.forEach(row => {
      const cells: TableCell[] = []
      row.querySelectorAll('th, td').forEach(cell => {
        cells.push({
          text: cell.textContent?.trim() || '',
          bold: true,
          fillColor: '#f0f0f0',
          fontSize: 7,
        })
      })
      if (cells.length > 0) {
        body.push(cells)
      }
    })
  }

  if (tbody) {
    const bodyRows = tbody.querySelectorAll('tr')

    bodyRows.forEach(row => {
      const cells: TableCell[] = []
      const rowStyle = row.getAttribute('style') || ''
      const rowFontSize = rowFontSizeFromStyle(rowStyle)

      row.querySelectorAll('td, th').forEach((cell, cellIndex) => {
        const classList = Array.from(cell.classList)
        const isHeader = cell.tagName.toLowerCase() === 'th'
        const shouldBeBold = isHeader || (isVerticalTable && cellIndex === 0)

        // Check if cell contains span elements with color
        const spans = cell.querySelectorAll('span')
        const hasColoredSpans = spans.length > 0

        let cellContent: any

        if (hasColoredSpans) {
          // When the cell wraps everything in a single flex container (e.g. the
          // "Total Purchase Amount" row), treat that container's top-level
          // children as groups separated by a gap, mirroring the source flex
          // `gap`. Otherwise walk the cell directly. Either way, nested colored
          // spans keep their pink accent (a shallow pass would flatten them).
          const wrapper = cell.children.length === 1 ? cell.children[0] : null
          const wrapperIsFlex =
            !!wrapper && (wrapper.getAttribute('style') || '').includes('flex')

          if (wrapper && wrapperIsFlex && wrapper.children.length > 1) {
            const runs: any[] = []
            Array.from(wrapper.children).forEach((groupEl, groupIndex) => {
              const groupRuns = collectColoredRuns(
                groupEl,
                rowFontSize,
                shouldBeBold,
              )
              if (groupIndex > 0 && groupRuns.length > 0) {
                runs.push({ text: '     ', fontSize: rowFontSize, bold: shouldBeBold })
              }
              runs.push(...groupRuns)
            })
            cellContent = runs
          } else {
            cellContent = collectColoredRuns(cell, rowFontSize, shouldBeBold)
          }
        } else {
          const rawText = cell.textContent?.trim() || ''
          cellContent = rawText
        }
        // pdfmake requires colSpan on the cell AND empty placeholder cells
        // for the spanned columns — otherwise it throws "Malformed table row".
        const colSpanAttr = cell.getAttribute('colspan')
        const colSpan = colSpanAttr ? parseInt(colSpanAttr, 10) : 1

        const rowStyleBg = rowStyle.match(/background-color:\s*([^;]+)/)?.[1]?.trim()

        cells.push({
          text: cellContent,
          bold: shouldBeBold,
          color: classList.includes('highlight-red') ? '#ee5959' : undefined,
          fontSize: rowFontSize,
          fillColor:
            shouldBeBold && isVerticalTable
              ? '#f9f9f9'
              : rowStyleBg ?? undefined,
          noWrap: false, // Allow text wrapping to prevent overflow
          ...(colSpan > 1 ? { colSpan } : {}),
        })
        for (let i = 1; i < colSpan; i++) {
          cells.push({
            text: '',
            fillColor: rowStyleBg ?? undefined,
          })
        }
      })
      if (cells.length > 0) {
        body.push(cells)
      }
    })
  }

  if (body.length === 0) return null

  const numColumns = body[0].length
  let widths: any[]

  // Determine if this is the special "Total Purchase Amount" vertical table
  let isTotalPurchaseTable = false

  if (isVerticalTable && numColumns === 2) {
    const firstCellText = body[0]?.[0]?.text
    if (typeof firstCellText === 'string') {
      isTotalPurchaseTable = firstCellText.includes('Total Purchase Amount')
    } else if (Array.isArray(firstCellText)) {
      const fullText = firstCellText.map(part => part.text || '').join('')
      isTotalPurchaseTable = fullText.includes('Total Purchase Amount')
    }

    widths = isTotalPurchaseTable ? [220, '*'] : [120, '*']
  } else {
    // Try to extract column widths from HTML table headers
    const headerRow = thead?.querySelector('tr')
    if (headerRow) {
      const headerCells = headerRow.querySelectorAll('th, td')
      const extractedWidths: any[] = []
      let hasExplicitWidths = false

      headerCells.forEach(cell => {
        const style = cell.getAttribute('style') || ''
        const widthMatch = style.match(/width:\s*(\d+)px/)
        if (widthMatch) {
          extractedWidths.push(parseInt(widthMatch[1], 10))
          hasExplicitWidths = true
        } else {
          extractedWidths.push('*')
        }
      })

      widths = hasExplicitWidths ? extractedWidths : Array(numColumns).fill('*')
    } else {
      // Equal widths for non-vertical tables so they span full width
      widths = Array(numColumns).fill('*')
    }
  }

  // Default border color (your existing behavior)
  const defaultBorderColor =
    parentContext?.sectionType === 'pink' ? '#f7006b' : '#d0d0d0'

  // Layout:
  //  - For the special table: 1px pink perimeter only (no inner grid).
  //  - For all others: your existing 0.5px grid with context-aware color.
  const layout = isTotalPurchaseTable
    ? {
        hLineWidth: (i: number, node: any) =>
          i === 0 || i === node.table.body.length ? 1 : 0,
        vLineWidth: (i: number, node: any) =>
          i === 0 || i === node.table.widths.length ? 1 : 0,

        hLineColor: () => '#F7006B',
        vLineColor: () => '#F7006B',

        fillColor: (rowIndex: number) => {
          if (rowIndex === 0 && thead) return '#f0f0f0'
          return null
        },
        paddingLeft: () => 3,
        paddingRight: () => 3,
        paddingTop: () => 2,
        paddingBottom: () => 2,
      }
    : {
        fillColor: (rowIndex: number) => {
          if (rowIndex === 0 && thead) return '#f0f0f0'
          return null
        },
        hLineWidth: () => 0.5,
        vLineWidth: () => 0.5,
        hLineColor: () => defaultBorderColor,
        vLineColor: () => defaultBorderColor,
        paddingLeft: () => 3,
        paddingRight: () => 3,
        paddingTop: () => 2,
        paddingBottom: () => 2,
      }

  return {
    table: {
      headerRows: thead ? thead.querySelectorAll('tr').length : 0,
      widths,
      body,
      dontBreakRows: false,
      keepWithHeaderRows: 6,
    },
    layout,
    margin: [0, 0, 0, 0] as [number, number, number, number], // Container controls spacing
    unbreakable: isVerticalTable, // Keep vertical tables (like wire instructions) together
  }
}

export const convertHtmlToPdf = async (
  htmlContent: string,
  filename: string,
  sourceDocument?: Document,
): Promise<void> => {
  try {
    const content = await parseHtmlToPdfContent(htmlContent, sourceDocument)

    const docDefinition: TDocumentDefinitions = {
      content,
      styles: {
        h1: {
          fontSize: 14,
          bold: true,
          color: '#668bd1',
        },
        h2: {
          fontSize: 11,
          bold: true,
          color: '#695bc7',
        },
        h2Red: {
          fontSize: 11,
          bold: true,
          color: '#ee5959',
        },
        h3: {
          fontSize: 10,
          bold: true,
          color: '#1a5276',
        },
        h4: {
          fontSize: 10,
          bold: true,
        },
      },
      defaultStyle: {
        fontSize: 8,
        font: 'Roboto',
        lineHeight: 1.3,
        color: '#1a1a1a',
      },
      pageSize: 'A4',
      pageMargins: [3, 3, 3, 3] as [number, number, number, number],
    }

    const pdfFilename = filename.endsWith('.pdf') ? filename : `${filename}.pdf`

    const pdfMake = await loadPdfMake()
    pdfMake.createPdf(docDefinition).download(pdfFilename)
  } catch (error) {
    console.error('Error converting HTML to PDF:', error)
    throw new Error('Failed to convert HTML to PDF')
  }
}

export const convertHtmlToPdfBlob = async (
  htmlContent: string,
  sourceDocument?: Document,
): Promise<string> => {
  try {
    const content = await parseHtmlToPdfContent(htmlContent, sourceDocument)

    const docDefinition: TDocumentDefinitions = {
      content,
      styles: {
        h1: {
          fontSize: 14,
          bold: true,
          color: '#668bd1',
        },
        h2: {
          fontSize: 11,
          bold: true,
          color: '#695bc7',
        },
        h2Red: {
          fontSize: 11,
          bold: true,
          color: '#ee5959',
        },
        h3: {
          fontSize: 10,
          bold: true,
          color: '#1a5276',
        },
        h4: {
          fontSize: 10,
          bold: true,
        },
      },
      defaultStyle: {
        fontSize: 8,
        font: 'Roboto',
        lineHeight: 1.3,
        color: '#1a1a1a',
      },
      pageSize: 'A4',
      pageMargins: [15, 15, 15, 15] as [number, number, number, number],
    }

    const pdfMake = await loadPdfMake()

    return new Promise((resolve, reject) => {
      try {
        pdfMake.createPdf(docDefinition).getBlob((blob: Blob) => {
          const blobUrl = URL.createObjectURL(blob)
          resolve(blobUrl)
        })
      } catch (error) {
        reject(error)
      }
    })
  } catch (error) {
    console.error('Error converting HTML to PDF blob:', error)
    throw new Error('Failed to convert HTML to PDF blob')
  }
}

export const convertIframeToPdf = async (
  iframe: HTMLIFrameElement,
  filename: string,
): Promise<void> => {
  try {
    if (!iframe.contentDocument) {
      throw new Error('Cannot access iframe content')
    }

    const htmlContent = iframe.contentDocument.documentElement.outerHTML

    // Pass the iframe's document so we can access already-loaded images
    await convertHtmlToPdf(htmlContent, filename, iframe.contentDocument)
  } catch (error) {
    console.error('Error converting iframe to PDF:', error)
    throw new Error('Failed to convert iframe content to PDF')
  }
}
